/**
 * CompleteStudyDialog — Confirmation dialog for completing a study.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';
import { recordStudyCompletion } from '../../stores/readingTempoStore';

interface CompleteStudyDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
}

export const CompleteStudyDialog = ({
  open,
  study,
  onClose,
  onComplete,
}: CompleteStudyDialogProps) => {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!study) return;
      await api.completeStudy(
        study.objKey,
        reason || undefined,
        undefined,
        comment || undefined,
      );
    },
    onSuccess: () => {
      if (study) recordStudyCompletion(study.acc || study.objKey);
      setReason('');
      setComment('');
      setError(null);
      onComplete?.();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  if (!study) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Study</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Mark <strong>{study.name}</strong> ({study.acc}) as complete?
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Comment (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          Complete Study
        </Button>
      </DialogActions>
    </Dialog>
  );
};
